import { useToProductTypeLabel } from 'components/common-form/builder/form/node/field/ProductListField';
import { ButtonsGroup } from 'components/shared/ButtonsGroup/ButtonsGroup';
import { CompactList } from 'components/shared/CompactList';
import { RawCompactList } from 'components/shared/CompactList/RawCompactList';
import { CurrencyFlagIcon, WithCountryFlag } from 'components/shared/WithCountryFlag';
import { AvailableItem, AvailableItems } from 'models/forms/fields/AvailableItemsField';
import { useCallback, useMemo } from 'react';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { userPreferencesValidation } from 'state/selectors';
import type { DefaultFieldValues } from 'state/userPreferences/userPreferences.model';

import type { DefaultFieldKey } from '../UserPreferencesForm.connect';
import { ErrorFeedback } from './ErrorFeedback';
import { FlexRowGroup } from './RowGroup';
import { SectionTitle, TabForm } from './TabForm';

interface ProductTabProps {
  onClose: () => void;
  selectedOnBehalfClient?: string;
  availableProductTypes: readonly string[];
  productType: Nullable<string>;
  productTypeValidity: userPreferencesValidation.ValidationStatus;
  availableProductSubtypes: readonly string[];
  productSubtype: Nullable<string>;

  productSubTypeValidity: userPreferencesValidation.ValidationStatus;
  availableWrappers: readonly string[];
  wrapper: Nullable<string>;

  wrapperValidity: userPreferencesValidation.ValidationStatus;
  currency: Nullable<string>;
  availableCurrencies: ReadonlyArray<string | null>;
  currencyValidity: userPreferencesValidation.ValidationStatus;
  onDefaultFieldChanged<K extends DefaultFieldKey>(key: K, value: DefaultFieldValues[K]): void;
}

function mapToAvailableItems(values: ReadonlyArray<string | null>, toLabel: (code: string) => string): AvailableItems {
  return values.map(
    x =>
      ({
        value: x,
        label: x === null ? '' : toLabel(x),
      }) as AvailableItem,
  );
}

export function ProductTabForm({
  onClose,
  productType,
  productSubtype,
  wrapper,
  currency,
  availableProductSubtypes,
  availableProductTypes,
  availableWrappers,
  availableCurrencies,
  currencyValidity,
  productSubTypeValidity,
  productTypeValidity,
  wrapperValidity,
  onDefaultFieldChanged,
}: ProductTabProps) {
  const toLabel = useToProductTypeLabel();

  const setProductType = useCallback(
    (value: any) => {
      logAnalytics.trackEvent('User preferences', 'Set product type', value);
      return onDefaultFieldChanged('productType', value);
    },
    [onDefaultFieldChanged],
  );
  const availableProductTypesItems = useMemo(
    () => mapToAvailableItems(availableProductTypes, toLabel),
    [availableProductTypes, toLabel],
  );

  const setProductSubType = useCallback(
    (value: any) => {
      logAnalytics.trackEvent('User preferences', 'Set product subtype', value);
      return onDefaultFieldChanged('productSubtype', value);
    },
    [onDefaultFieldChanged],
  );
  const availableProductSubtypesItems = useMemo(
    () => mapToAvailableItems(availableProductSubtypes, toLabel),
    [availableProductSubtypes, toLabel],
  );
  const availableCurrenciesItems = useMemo(
    () => mapToAvailableItems(availableCurrencies, toLabel),
    [availableCurrencies, toLabel],
  );

  const setWrapper = useCallback(
    (value: any) => {
      logAnalytics.trackEvent('User preferences', 'Set wrapper', value);
      return onDefaultFieldChanged('wrapper', value);
    },
    [onDefaultFieldChanged],
  );

  const setCurrency = useCallback(
    (value: any) => {
      const newValue = value === '' ? null : value;
      logAnalytics.trackEvent('User preferences', 'Set currency', newValue);
      return onDefaultFieldChanged('currency', newValue);
    },
    [onDefaultFieldChanged],
  );

  return (
    <TabForm labelKey="userPreferencesForm.products.label" onClose={onClose}>
      <SectionTitle labelKey="userPreferencesForm.defaultValues.label" />
      {productTypeValidity !== 'hidden' && (
        <FlexRowGroup labelKey="form.Product" isFirst>
          <CompactList
            id="productType"
            value={productType}
            onValueChanged={setProductType}
            availableItems={availableProductTypesItems}
            isDisabled={false}
            isInvalid={productTypeValidity === 'invalid'}
          />
          {productTypeValidity === 'invalid' && <ErrorFeedback />}
        </FlexRowGroup>
      )}
      {productSubTypeValidity !== 'hidden' && (
        <FlexRowGroup labelKey="form.ProductSubtype" isFirst={false}>
          <CompactList
            id="productSubtype"
            value={productSubtype}
            onValueChanged={setProductSubType}
            availableItems={availableProductSubtypesItems}
            isDisabled={false}
            isInvalid={productSubTypeValidity === 'invalid'}
          />
          {productSubTypeValidity === 'invalid' && <ErrorFeedback />}
        </FlexRowGroup>
      )}
      {wrapperValidity !== 'hidden' && (
        <FlexRowGroup labelKey="form.Wrapper" isFirst={false}>
          <ButtonsGroup
            className="w-100"
            items={availableWrappers}
            selectedItems={wrapper}
            onSelect={items => setWrapper(items[0])}
            isRadio
            translationPrefix="form.Wrapper"
            isInvalid={wrapperValidity === 'invalid'}
          />
          {wrapperValidity === 'invalid' && <ErrorFeedback />}
        </FlexRowGroup>
      )}
      {currencyValidity !== 'hidden' && (
        <FlexRowGroup labelKey="form.Currency" isFirst={false}>
          <WithCountryFlag content={currency ? <CurrencyFlagIcon currency={currency} /> : undefined}>
            <RawCompactList
              id="currency"
              value={currency}
              onValueChanged={setCurrency}
              availableItems={availableCurrenciesItems}
              isDisabled={false}
              isInvalid={currencyValidity === 'invalid'}
            />
          </WithCountryFlag>
          {currencyValidity === 'invalid' && <ErrorFeedback />}
        </FlexRowGroup>
      )}
    </TabForm>
  );
}

ProductTabForm.displayName = 'ProductTabForm';
